/* Spinning-vinyl hero interaction.
   The idle rotation is pure CSS (robust: keeps spinning even where JS frames
   are throttled). This script takes over only when the visitor interacts —
   seeding from the current CSS angle so there is no visual jump — to add:
     • slow-down on hover,
     • subtle pointer-follow,
     • drag/flick momentum, easing back to normal speed.
   Never engages under prefers-reduced-motion or when animation is disabled. */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function currentAngle(el) {
    var t = getComputedStyle(el).transform;
    if (!t || t === 'none') return 0;
    try {
      var m = new DOMMatrixReadOnly(t);
      return Math.atan2(m.b, m.a) * (180 / Math.PI);
    } catch (e) {
      return 0;
    }
  }

  function initHero(art) {
    if (art.dataset.heroInit) return;
    if (art.dataset.animate !== 'true' || reduced.matches) return;

    var vinyl = art.querySelector('.hero-v__vinyl');
    var track = art.querySelector('.hero-v__vinyl-track');
    if (!vinyl || !track) return;
    /* rotor = the element that actually spins (grooves layer for the drawn
       record, the image itself otherwise); pointer events stay on `vinyl`. */
    var rotor = art.querySelector('[data-vinyl-rotor]') || vinyl;

    art.dataset.heroInit = '1';

    var direction = Number(art.dataset.direction) || 1;
    var secondsPerRev = Math.max(2, Number(art.dataset.speed) || 22);
    var baseVelocity = (360 / secondsPerRev) * direction; // deg/s
    var velocity = baseVelocity;
    var targetFactor = 1;   // 1 = normal, 0.15 = hover-slowed
    var dragBoost = 0;      // extra deg/s from dragging, decays
    var angle = 0;
    var tiltX = 0, tiltY = 0, targetTiltX = 0, targetTiltY = 0;
    var lastT = null;
    var running = false;    // rAF loop state
    var jsDriven = false;   // whether we've taken over from CSS
    var visible = true;

    function frame(t) {
      if (!running) return;
      if (lastT === null) lastT = t;
      var dt = Math.min(0.1, (t - lastT) / 1000);
      lastT = t;

      var target = baseVelocity * targetFactor + dragBoost;
      velocity += (target - velocity) * Math.min(1, dt * 4);
      dragBoost *= Math.pow(0.2, dt);
      angle = (angle + velocity * dt) % 360;

      tiltX += (targetTiltX - tiltX) * Math.min(1, dt * 6);
      tiltY += (targetTiltY - tiltY) * Math.min(1, dt * 6);

      rotor.style.transform = 'rotate(' + angle.toFixed(2) + 'deg)';
      track.style.translate = tiltX.toFixed(1) + 'px ' + tiltY.toFixed(1) + 'px';
      requestAnimationFrame(frame);
    }

    /* Switch from the CSS animation to JS control without a visible jump.
       Only called from real user interaction, so rAF is known to be alive. */
    function takeover() {
      if (jsDriven) return;
      jsDriven = true;
      angle = currentAngle(rotor);
      art.classList.add('is-js-driven');
      startLoop();
    }

    function startLoop() {
      if (!jsDriven || running || !visible) return;
      running = true;
      lastT = null;
      requestAnimationFrame(frame);
    }

    /* Pause the loop off-screen; the CSS animation pauses itself for free. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (!visible) running = false;
        else startLoop();
      }).observe(art);
    }

    /* hover: slow to a crawl, like resting a finger near the platter */
    art.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      takeover();
      targetFactor = 0.15;
    });
    art.addEventListener('pointerleave', function () {
      targetFactor = 1;
      targetTiltX = 0;
      targetTiltY = 0;
    });

    /* subtle pointer-follow */
    art.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      takeover();
      var rect = art.getBoundingClientRect();
      targetTiltX = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
      targetTiltY = ((e.clientY - rect.top) / rect.height - 0.5) * 6;
    });

    /* drag/flick to spin. touch-action: pan-y in CSS keeps page scroll intact;
       we only react to horizontal movement. */
    var dragging = false;
    var lastX = 0;

    vinyl.addEventListener('pointerdown', function (e) {
      takeover();
      dragging = true;
      lastX = e.clientX;
      if (e.pointerType === 'mouse') {
        vinyl.setPointerCapture(e.pointerId);
        e.preventDefault();
      }
    });
    vinyl.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - lastX;
      lastX = e.clientX;
      dragBoost += dx * 14 * direction;
      dragBoost = Math.max(-1440, Math.min(1440, dragBoost));
    });
    function endDrag() { dragging = false; }
    vinyl.addEventListener('pointerup', endDrag);
    vinyl.addEventListener('pointercancel', endDrag);

    /* slow the platter while keyboard focus is inside the hero */
    var hero = art.closest('.hero-v');
    if (hero) {
      hero.addEventListener('focusin', function () {
        if (jsDriven) targetFactor = 0.15;
      });
      hero.addEventListener('focusout', function () {
        targetFactor = 1;
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-hero-vinyl]').forEach(function (el) { initHero(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  /* re-init inside the theme editor */
  document.addEventListener('shopify:section:load', initAll);
})();
